import {
    formatPayerAdjustmentGroupLabel,
    formatPayerAdjustmentGroupShortCode,
} from '../../../services/dictionary/payer-adjustment-groups';
import {
    formatMatchBlockSummary,
    normalizePaymentMatchValue,
} from '../../../services/worker-configuration/automation/worker-automation-scope.registry';
import {
    AutomationRule,
    RuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    getActionMeta,
    resolveRuleActionType,
} from '../../../services/worker-configuration/automation/worker-automation.utils';

export type RulesGroupBy = 'PRIORITY' | 'ACTION' | 'CARC';
export type RulesActionFilter = RuleActionType | 'ALL';

export interface RuleCodeChip {
    kind: 'carc' | 'rarc';
    label: string;
}

export interface RuleTableRow {
    actionType: RuleActionType | null;
    codeChips: RuleCodeChip[];
    /** Summaries of every non-Payment filter group, joined with AND. */
    otherConditions: string[];
    /** 1-based position in the worker's full rule list — the evaluation order. */
    priority: number;
    rule: AutomationRule;
}

export interface RuleTableGroup {
    key: string;
    /** Null for the single ungrouped PRIORITY list. */
    label: string | null;
    rows: RuleTableRow[];
}

type MatchSummaryLookups = Parameters<typeof formatMatchBlockSummary> extends [unknown, ...infer Rest]
    ? Rest
    : never;

export const RULE_ACTION_ORDER: RuleActionType[] = [
    'CLOSE_OUT',
    'SWITCH_TO_PRIVATE_PAY',
    'CREATE_APPEAL',
    'CREATE_MESSAGE',
    'EXECUTE_ENTER_FUNCTION',
];

const CARC_GROUP_ORDER = ['CO', 'PR', 'OA', 'PIR', 'CR'];
const CODE_ONLY_KEY = 'CODE_ONLY';
const RARC_ONLY_KEY = 'RARC_ONLY';
const NO_CODES_KEY = 'NONE';

/**
 * CARC chips carry their group when one is set — `CO-45` and `PR-45` are opposite outcomes
 * (written off vs owed by the patient), so the group is never dropped from the label.
 */
function buildCodeChips(rule: AutomationRule): RuleCodeChip[] {
    const chips: RuleCodeChip[] = [];

    rule.match
        .filter(match => match.scope === 'PAYMENT')
        .forEach(match => {
            const payment = normalizePaymentMatchValue(match.value);
            const groups = payment.carc.group.map(formatPayerAdjustmentGroupShortCode);
            const prefix = groups.length ? `${groups.join('/')}-` : '';

            if (payment.carc.codes.length) {
                payment.carc.codes.forEach(code => chips.push({ kind: 'carc', label: `${prefix}${code}` }));
            } else {
                groups.forEach(group => chips.push({ kind: 'carc', label: group }));
            }
            payment.rarc.codes.forEach(code => chips.push({ kind: 'rarc', label: code }));
        });

    return chips;
}

export function buildRuleTableRows(
    rules: AutomationRule[],
    showCodeColumn: boolean,
    ...lookups: MatchSummaryLookups
): RuleTableRow[] {
    return rules.map((rule, index) => ({
        rule,
        priority: index + 1,
        actionType: resolveRuleActionType(rule.action.type),
        codeChips: showCodeColumn ? buildCodeChips(rule) : [],
        otherConditions: rule.match
            .filter(match => !showCodeColumn || match.scope !== 'PAYMENT')
            .map(match => formatMatchBlockSummary(match, ...lookups))
            .filter(summary => !summary.endsWith(': not set')),
    }));
}

export function matchesRuleQuery(row: RuleTableRow, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle) {
        return true;
    }

    const haystack = [
        row.rule.name,
        getActionMeta(row.rule.action.type).label,
        ...row.codeChips.map(chip => chip.label),
        ...row.otherConditions,
    ]
        .join(' ')
        .toLowerCase();

    return haystack.includes(needle);
}

function carcGroupKey(row: RuleTableRow): string {
    const groups = new Set<string>();
    let hasCode = false;
    let hasRarc = false;

    row.rule.match
        .filter(match => match.scope === 'PAYMENT')
        .forEach(match => {
            const payment = normalizePaymentMatchValue(match.value);
            payment.carc.group.forEach(group => groups.add(formatPayerAdjustmentGroupShortCode(group)));
            hasCode = hasCode || payment.carc.codes.length > 0;
            hasRarc = hasRarc || payment.rarc.codes.length > 0;
        });

    if (groups.size) {
        return [...groups].sort(compareCarcGroups).join(' + ');
    }
    if (hasCode) {
        return CODE_ONLY_KEY;
    }
    return hasRarc ? RARC_ONLY_KEY : NO_CODES_KEY;
}

function compareCarcGroups(a: string, b: string): number {
    const rank = (value: string): number => {
        const index = CARC_GROUP_ORDER.indexOf(value.split(' + ')[0]);
        return index === -1 ? CARC_GROUP_ORDER.length : index;
    };

    return rank(a) - rank(b) || a.localeCompare(b);
}

function carcGroupLabel(key: string): string {
    switch (key) {
        case CODE_ONLY_KEY:
            return 'CARC code, any group';
        case RARC_ONLY_KEY:
            return 'RARC only';
        case NO_CODES_KEY:
            return 'No denial code condition';
        default:
            return key.includes(' + ') ? key : `${key} · ${formatPayerAdjustmentGroupLabel(key)}`;
    }
}

export function groupRuleTableRows(rows: RuleTableRow[], groupBy: RulesGroupBy): RuleTableGroup[] {
    if (groupBy === 'PRIORITY') {
        return [{ key: 'all', label: null, rows }];
    }

    const buckets = new Map<string, RuleTableRow[]>();
    rows.forEach(row => {
        const key = groupBy === 'ACTION' ? (row.actionType ?? row.rule.action.type) : carcGroupKey(row);
        buckets.set(key, [...(buckets.get(key) ?? []), row]);
    });

    const keys = [...buckets.keys()];
    if (groupBy === 'ACTION') {
        const rank = (key: string): number => {
            const index = RULE_ACTION_ORDER.indexOf(key as RuleActionType);
            return index === -1 ? RULE_ACTION_ORDER.length : index;
        };
        keys.sort((a, b) => rank(a) - rank(b));
    } else {
        const tail = [CODE_ONLY_KEY, RARC_ONLY_KEY, NO_CODES_KEY];
        keys.sort((a, b) => {
            const ta = tail.indexOf(a);
            const tb = tail.indexOf(b);
            if (ta !== -1 || tb !== -1) {
                return (ta === -1 ? -1 : ta) - (tb === -1 ? -1 : tb);
            }
            return compareCarcGroups(a, b);
        });
    }

    return keys.map(key => ({
        key,
        label: groupBy === 'ACTION' ? getActionMeta(key).label : carcGroupLabel(key),
        rows: buckets.get(key) ?? [],
    }));
}
